import {
  UsersUserGroupsGroupedDropdownInput,
  UsersUserGroupsGroupedDropdownInputMode,
  UsersUserGroupsGroupedDropdownInputModeType,
} from "#src/batteries-included-components/Dropdowns/UsersUserGroupsGroupedDropdownInput";
import {
  WorkflowConfigurationDrawerAssignmentFilterFilterArea,
  WorkflowConfigurationDrawerAssignmentFilterFiltersType,
} from "#src/batteries-included-components/FilterAreas/WorkflowConfigurationDrawerAssignmentFilterFilterAreas";
import { useWorkflowGraphState } from "#src/batteries-included-components/Graph/WorkflowGraph/WorkflowGraphCanvas/WorkflowGraphStore";
import { UserDataDisplay } from "#src/components/Common/UserDataDisplay";
import { UserGroupDataDisplay } from "#src/components/Common/UserGroupDataDisplay";
import { useListCustomAttributes } from "#src/components/hooks/adapters/useCustomAttributes";
import useLocalization from "#src/hooks/useLocalization";
import { useStickyState } from "#src/hooks/useStickyState";
import { useStorageKey } from "#src/hooks/useStorageKey";
import { useQuery } from "@tanstack/react-query";
import {
  Accordion,
  Button,
  Card,
  Dialog,
  DialogProps,
  DropdownInputBare,
  Form,
  GroupedDropdownValueType,
  Radio,
  Skeleton,
  TextInput,
  useForm,
} from "@validereinc/common-components";
import {
  AssetTypeType,
  AttributeDataType,
  UserGroupsAdapter,
  UsersAdapter,
  WorkflowStepType,
  WorkflowTaskAssignee,
  WorkflowTaskAssignmentFilter,
  WorkflowUserTaskType,
} from "@validereinc/domain";
import classNames from "classnames/bind";
import { isEmpty } from "es-toolkit/compat";
import React, { useEffect, useMemo, useState } from "react";
import {
  type AssigneeFormValuesType,
  useWorkflowConfigurationFormStore,
} from "../WorkflowConfigurationFormStore";
import styles from "./WorkflowConfigurationAssignee.module.scss";

const cx = classNames.bind(styles);

type WorkflowConfigurationAssigneeFormValues = Pick<
  WorkflowUserTaskType,
  "assignee_user" | "assignee_group" | "assignment_filter"
> & {
  assignee?:
    | [group: UsersUserGroupsGroupedDropdownInputModeType, value: string]
    | null;
};

type AreAssignmentFilterAssigneesGroupType = Record<string, boolean>;

type AssignmentMode =
  | "userOrGroup"
  | "assignmentFilterCustomAttributes"
  | "assignmentFilterVariable";

const AddNewVariableConditionDialog = ({
  onAdd,
  isOpen,
  onClose,
}: {
  onAdd: (variableValue: string, assignmentRule: WorkflowTaskAssignee) => void;
} & Pick<DialogProps, "isOpen" | "onClose">) => {
  const form = useForm<{
    assignee:
      | [group: UsersUserGroupsGroupedDropdownInputModeType, value: string]
      | null;
    variableValue: string;
  }>();

  const [isGroup, setIsGroup] = useState(false);
  const formValues = form.watch();

  const isFormValid = useMemo(
    () => Boolean(formValues.assignee?.[1] && formValues.variableValue),
    [JSON.stringify(formValues)]
  );

  useEffect(() => {
    if (isOpen) form.reset({ assignee: null, variableValue: "" });
  }, [isOpen]);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Add Variable Condition"
      actionRow={[
        <Button
          key="add-button"
          variant="primary"
          disabled={!isFormValid}
          onClick={() => {
            if (!isFormValid) return;
            const cleanValue: WorkflowTaskAssignee = {};
            if (formValues.assignee?.[0] === "users")
              cleanValue.assignee_user = formValues.assignee[1];

            if (formValues.assignee?.[0] === "groups")
              cleanValue.assignee_group = formValues.assignee[1];

            if (Object.values(cleanValue).filter((e) => !!e).length === 0)
              return;

            onAdd(formValues.variableValue, cleanValue);
          }}
        >
          Add
        </Button>,
      ]}
    >
      <Form {...form}>
        <TextInput
          name="variableValue"
          label="Variable Value"
          isRequired
        />
        <UsersUserGroupsGroupedDropdownInput
          name="assignee"
          label="Variable Assignee"
          defaultGroup={
            isGroup
              ? UsersUserGroupsGroupedDropdownInputMode.GROUPS
              : UsersUserGroupsGroupedDropdownInputMode.USERS
          }
          onActiveGroupChange={setIsGroup}
          isRequired
          isFluid
        />
      </Form>
    </Dialog>
  );
};

const SingleAssigneeRender = ({
  assignee,
}: {
  assignee:
    | [group: UsersUserGroupsGroupedDropdownInputModeType, value: string]
    | null;
}) => {
  const isUserEnabled = assignee?.[0] === "users" && !!assignee?.[1];
  const isGroupEnabled = assignee?.[0] === "groups" && !!assignee?.[1];
  const id = assignee?.[1] ?? "";

  const userQuery = useQuery({
    queryKey: ["users", id],
    queryFn: () => UsersAdapter.getOne({ id }),
    enabled: isUserEnabled,
  });

  const groupQuery = useQuery({
    queryKey: ["groups", id],
    queryFn: () => UserGroupsAdapter.getOne({ id }),
    enabled: isGroupEnabled,
  });

  const isLoading =
    (isUserEnabled && userQuery.isLoading) ||
    (isGroupEnabled && groupQuery.isLoading);

  return (
    <Skeleton
      isLoading={isLoading}
      style={{ height: 24, width: 96 }}
    >
      {isUserEnabled && userQuery.data?.data && (
        <UserDataDisplay
          id={id}
          name={userQuery.data?.data.name}
        />
      )}
      {isGroupEnabled && groupQuery.data?.data && (
        <UserGroupDataDisplay
          id={id}
          name={groupQuery.data.data.name}
        />
      )}
    </Skeleton>
  );
};

const customAttributeFormValuesPrefix = "custom_attribute_";
const variableFormValuesPrefix = "variable_";

const WorkflowConfigurationAssignee = ({
  stepDefinition,
  assetType,
  stepId,
}: {
  stepDefinition?: WorkflowUserTaskType;
  assetType?: AssetTypeType;
  stepId: string;
}) => {
  const { setAssignee, setAssigneeIsValid } =
    useWorkflowConfigurationFormStore();

  const isStepUserTask = stepDefinition?.type === WorkflowStepType.USER_TASK;
  const assignmentFilter = isStepUserTask
    ? stepDefinition?.assignment_filter
    : {};
  // assignmentFilter is currently restricted to having a single key
  const assignmentFilterKey = assignmentFilter
    ? Object.keys(assignmentFilter)[0]
    : "";

  const { localize } = useLocalization();
  const [assignmentMode, setAssignmentMode] = useState<AssignmentMode>(() => {
    if (assignmentFilterKey) {
      return assignmentFilterKey.startsWith("$")
        ? "assignmentFilterVariable"
        : "assignmentFilterCustomAttributes";
    }
    return "userOrGroup";
  });
  const [
    areCustomAttributeAssignmentFilterAssigneesGroup,
    setAreCustomAttributeAssignmentFilterAssigneesGroup,
  ] = useState<AreAssignmentFilterAssigneesGroupType>({});

  const [customAttributeFieldName, setCustomAttributeFieldName] = useState("");

  const [selectedVariable, setSelectedVariable] = useState("");
  const [
    isNewVariableConditionDialogOpen,
    setIsNewVariableConditionDialogOpen,
  ] = useState(false);

  const [filterConfigStorageKey] = useStorageKey(
    ["filter-configuration"],
    ["workflows", "configuration", "assignee"]
  );

  const [assignmentFilterFilters] =
    useStickyState<WorkflowConfigurationDrawerAssignmentFilterFiltersType>(
      {} as WorkflowConfigurationDrawerAssignmentFilterFiltersType,
      filterConfigStorageKey
    );

  const customAttributeQueryFilters = {
    entity_type: assetType,
  };

  const { data: customAttributeData } = useListCustomAttributes({
    filters: customAttributeQueryFilters,
  });

  const processCustomAttributeData = useMemo(() => {
    const customAttributesWithPickList =
      customAttributeData?.data.filter(
        (customAttribute) =>
          customAttribute.data_type === AttributeDataType.PICK_LIST
      ) ?? [];

    const selectedCustomAttribute = customAttributesWithPickList?.find(
      (customAttribute) =>
        customAttribute.field_name === customAttributeFieldName
    );
    const allCustomAttributePickListValues =
      selectedCustomAttribute?.pick_list_values ?? [];

    const filteredCustomAttributePickListValues =
      allCustomAttributePickListValues.filter((customAttributeValue) =>
        assignmentFilterFilters.customAttributeValue
          ? customAttributeValue
              .toLowerCase()
              .includes(
                assignmentFilterFilters.customAttributeValue.toLowerCase()
              )
          : customAttributeValue
      );

    const customAttributeDisplayName = selectedCustomAttribute?.display_name;

    return {
      customAttributesWithPickList,
      allCustomAttributePickListValues,
      filteredCustomAttributePickListValues,
      customAttributeDisplayName,
    };
  }, [customAttributeData, customAttributeFieldName, assignmentFilterFilters]);

  const {
    customAttributesWithPickList,
    allCustomAttributePickListValues,
    filteredCustomAttributePickListValues,
    customAttributeDisplayName,
  } = processCustomAttributeData;

  const { getAvailableStepVariables } = useWorkflowGraphState();

  const variablesAvailable = getAvailableStepVariables(stepId);

  const variableOptions = useMemo(() => {
    return variablesAvailable.map((value) => ({ value }));
  }, [JSON.stringify(variablesAvailable)]);

  const form = useForm<WorkflowConfigurationAssigneeFormValues>({
    defaultValues: stepDefinition
      ? {
          assignee: stepDefinition.assignee_user
            ? [
                UsersUserGroupsGroupedDropdownInputMode.USERS,
                stepDefinition.assignee_user ?? "",
              ]
            : stepDefinition.assignee_group
              ? [
                  UsersUserGroupsGroupedDropdownInputMode.GROUPS,
                  stepDefinition.assignee_group ?? "",
                ]
              : null,
          ...(!isEmpty(assignmentFilter) &&
          customAttributeFieldName &&
          !assignmentFilterKey.includes("$") &&
          assignmentMode === "assignmentFilterCustomAttributes"
            ? {
                ...allCustomAttributePickListValues.reduce(
                  (
                    acc: Record<string, GroupedDropdownValueType>,
                    customAttributeValue
                  ) => {
                    const assignee =
                      assignmentFilter[assignmentFilterKey]?.[
                        customAttributeValue
                      ];

                    if (assignee?.assignee_user) {
                      acc[
                        `${customAttributeFormValuesPrefix}${customAttributeValue}`
                      ] = [
                        UsersUserGroupsGroupedDropdownInputMode.USERS,
                        assignee.assignee_user,
                      ];
                    } else if (assignee?.assignee_group) {
                      acc[
                        `${customAttributeFormValuesPrefix}${customAttributeValue}`
                      ] = [
                        UsersUserGroupsGroupedDropdownInputMode.GROUPS,
                        assignee.assignee_group,
                      ];
                    }

                    return acc;
                  },
                  {} as Record<string, GroupedDropdownValueType>
                ),
              }
            : {}),
          ...(!isEmpty(assignmentFilter) &&
          selectedVariable &&
          assignmentFilterKey.includes("$") &&
          assignmentMode === "assignmentFilterVariable"
            ? {
                ...Object.keys(
                  stepDefinition?.assignment_filter?.[assignmentFilterKey] ?? {}
                ).reduce(
                  (
                    acc: Record<string, GroupedDropdownValueType>,
                    variableValue
                  ) => {
                    const assignee =
                      assignmentFilter[assignmentFilterKey]?.[variableValue];

                    if (assignee?.assignee_user) {
                      acc[`${variableFormValuesPrefix}${variableValue}`] = [
                        UsersUserGroupsGroupedDropdownInputMode.USERS,
                        assignee.assignee_user,
                      ];
                    } else if (assignee?.assignee_group) {
                      acc[`${variableFormValuesPrefix}${variableValue}`] = [
                        UsersUserGroupsGroupedDropdownInputMode.GROUPS,
                        assignee.assignee_group,
                      ];
                    }

                    return acc;
                  },
                  {} as Record<string, GroupedDropdownValueType>
                ),
              }
            : {}),
        }
      : undefined,
  });

  const formValues = form.watch();

  const areAssigneesValid = useMemo(() => {
    if (assignmentMode === "assignmentFilterCustomAttributes")
      return (
        Boolean(allCustomAttributePickListValues.length) &&
        allCustomAttributePickListValues.every((customAttributeValue) => {
          const assignee =
            formValues[
              `${customAttributeFormValuesPrefix}${customAttributeValue}`
            ]?.[1];
          return Boolean(assignee);
        })
      );

    if (assignmentMode === "assignmentFilterVariable") {
      return (
        !!selectedVariable &&
        Object.entries(formValues).filter(
          ([key, value]) => key.startsWith(variableFormValuesPrefix) && !!value
        ).length > 0
      );
    }

    if (assignmentMode === "userOrGroup")
      return Boolean(formValues.assignee?.[1]);

    return false;
  }, [assignmentMode, allCustomAttributePickListValues, formValues]);

  const availableVariables = useMemo(
    () =>
      Object.entries(formValues)
        .filter(
          ([formEntryKey, formEntryValue]) =>
            formEntryKey.startsWith(variableFormValuesPrefix) &&
            formEntryValue !== undefined
        )
        .map(([entry]) => entry.replace(variableFormValuesPrefix, "")),
    [JSON.stringify(formValues)]
  );

  useEffect(() => {
    let assignmentFilter: WorkflowTaskAssignmentFilter | null = null;

    if (assignmentMode === "assignmentFilterCustomAttributes") {
      const customAttributeAssignments =
        allCustomAttributePickListValues.reduce(
          (acc, customAttributeValue) => {
            const assigneeType =
              formValues[
                `${customAttributeFormValuesPrefix}${customAttributeValue}`
              ]?.[0];
            const assigneeValue =
              formValues[
                `${customAttributeFormValuesPrefix}${customAttributeValue}`
              ]?.[1];

            if (
              assigneeType === UsersUserGroupsGroupedDropdownInputMode.USERS &&
              assigneeValue
            ) {
              acc[customAttributeValue] = { assignee_user: assigneeValue };
            } else if (
              assigneeType === UsersUserGroupsGroupedDropdownInputMode.GROUPS &&
              assigneeValue
            ) {
              acc[customAttributeValue] = { assignee_group: assigneeValue };
            }
            return acc;
          },
          {} as Record<
            string,
            { assignee_user?: string; assignee_group?: string }
          >
        );

      // Only create assignment filter if there are valid assignments
      if (Object.keys(customAttributeAssignments).length > 0) {
        assignmentFilter = {
          [`${assetType}.custom_attributes.${customAttributeFieldName}`]:
            customAttributeAssignments,
        };
      }
    }

    if (assignmentMode === "assignmentFilterVariable") {
      const variableAssignments = availableVariables.reduce(
        (acc, variableValue) => {
          const assigneeType =
            formValues[`${variableFormValuesPrefix}${variableValue}`]?.[0];
          const assigneeValue =
            formValues[`${variableFormValuesPrefix}${variableValue}`]?.[1];

          if (
            assigneeType === UsersUserGroupsGroupedDropdownInputMode.USERS &&
            assigneeValue
          ) {
            acc[variableValue] = { assignee_user: assigneeValue };
          } else if (
            assigneeType === UsersUserGroupsGroupedDropdownInputMode.GROUPS &&
            assigneeValue
          ) {
            acc[variableValue] = { assignee_group: assigneeValue };
          }
          return acc;
        },
        {} as Record<
          string,
          { assignee_user?: string; assignee_group?: string }
        >
      );

      // Only create assignment filter if there are valid assignments
      if (Object.keys(variableAssignments).length > 0) {
        assignmentFilter = {
          [selectedVariable]: variableAssignments,
        };
      }
    }

    let assigneeFormValues: AssigneeFormValuesType | undefined = undefined;

    if (assignmentFilter) {
      assigneeFormValues = {
        assignment_filter: assignmentFilter,
        assignee: null,
        assignee_group: null,
        assignee_user: null,
      };
    } else if (formValues.assignee?.[1]) {
      // Only set assignee if there's a valid value
      if (formValues.assignee[0] === "users") {
        assigneeFormValues = {
          assignee: formValues.assignee,
          assignee_user: formValues.assignee[1],
          assignee_group: null,
          assignment_filter: null,
        };
      } else if (formValues.assignee[0] === "groups") {
        assigneeFormValues = {
          assignee: formValues.assignee,
          assignee_group: formValues.assignee[1],
          assignee_user: null,
          assignment_filter: null,
        };
      }
    }

    // Only call setAssignee if we have valid assignee form values
    if (assigneeFormValues) {
      setAssignee(assigneeFormValues);
    }
  }, [JSON.stringify(formValues), setAssignee]);

  useEffect(() => {
    setAssigneeIsValid(form.formState.isValid && areAssigneesValid);
  }, [form.formState.isValid, areAssigneesValid, setAssigneeIsValid]);

  useEffect(() => {
    if (!isStepUserTask) {
      return;
    }

    if (!isEmpty(assignmentFilter) && assignmentFilter[assignmentFilterKey]) {
      const newAreAssignmentFilterAssigneesGroupType = Object.entries(
        assignmentFilter[assignmentFilterKey]
      ).reduce((acc, [attributeValue, assignee]) => {
        acc[attributeValue] = Boolean(assignee.assignee_group);
        return acc;
      }, {} as AreAssignmentFilterAssigneesGroupType);

      if (assignmentFilterKey.startsWith("$")) {
        setSelectedVariable(assignmentFilterKey);
        setAssignmentMode("assignmentFilterVariable");
      } else {
        setAreCustomAttributeAssignmentFilterAssigneesGroup(
          newAreAssignmentFilterAssigneesGroupType
        );
        setCustomAttributeFieldName(
          assignmentFilterKey.split(".").at(-1) ?? ""
        );
        setAssignmentMode("assignmentFilterCustomAttributes");
      }
    } else {
      setAssignmentMode("userOrGroup");
    }
  }, [stepDefinition]);

  return (
    <>
      <Form {...form}>
        <div className={cx("form-container")}>
          <Accordion defaultActiveKeys={["assignees"]}>
            <Accordion.AccordionPanel
              dataKey="assignees"
              title="Assignees"
            >
              <p className={cx("prompt", "radio")}>Assignment by Facilities</p>
              <Radio
                name="assignmentMode"
                value={assignmentMode}
                onChange={(newValue: string) => {
                  if (newValue === "assignmentFilterCustomAttributes") {
                    Object.keys(formValues)
                      .filter((key) => key.startsWith(variableFormValuesPrefix))
                      .forEach((key) => {
                        //@ts-expect-error Key doesn't exist in form values
                        form.setValue(key, undefined);
                      });
                    form.setValue("assignee", null);
                  }

                  if (newValue === "assignmentFilterVariable") {
                    Object.keys(formValues)
                      .filter((key) =>
                        key.startsWith(customAttributeFormValuesPrefix)
                      )
                      .forEach((key) => {
                        //@ts-expect-error Key doesn't exist in form values
                        form.setValue(key, undefined);
                      });
                    form.setValue("assignee", null);
                  }
                  if (newValue === "userOrGroup") {
                    Object.keys(formValues)
                      .filter(
                        (key) =>
                          key.startsWith(customAttributeFormValuesPrefix) ||
                          key.startsWith(variableFormValuesPrefix)
                      )
                      .forEach((key) => {
                        //@ts-expect-error Key doesn't exist in form values
                        form.setValue(key, undefined);
                      });
                    form.setValue("assignment_filter", null);
                  }

                  setAssignmentMode(newValue as AssignmentMode);
                }}
                options={[
                  {
                    label: `Assign across all ${localize(
                      `${assetType ?? ""}_plural`
                    ).toLowerCase()} in template`,
                    value: "userOrGroup" as AssignmentMode,
                  },
                  {
                    label: `Assign by ${localize(`${assetType ?? ""}`).toLowerCase()} filters`,
                    value: "assignmentFilterCustomAttributes" as AssignmentMode,
                  },
                  {
                    label: `Assign by workflow variables`,
                    value: "assignmentFilterVariable" as AssignmentMode,
                  },
                ]}
                labelKey="label"
                valueKey="value"
                isDisabled={false}
                isLoading={false}
              />

              {assignmentMode === "assignmentFilterCustomAttributes" && (
                <>
                  <div className={cx("field")}>
                    <p className={cx("prompt", "dropdown")}>
                      Attribute to assign by
                    </p>
                    <DropdownInputBare
                      name="customAttributeFieldName"
                      placeholder="Select an attribute..."
                      value={customAttributeFieldName}
                      onChange={(newValue) => {
                        setCustomAttributeFieldName(newValue);
                      }}
                      options={customAttributesWithPickList}
                      labelKey="display_name"
                      valueKey="field_name"
                    />
                  </div>
                  <div className={cx("field")}>
                    <WorkflowConfigurationDrawerAssignmentFilterFilterArea
                      customAttributeDisplayName={
                        customAttributeDisplayName ?? ""
                      }
                      filterConfigStorageKey={filterConfigStorageKey}
                    />
                  </div>
                  {filteredCustomAttributePickListValues.map(
                    (customAttributeValue) => {
                      return (
                        <UsersUserGroupsGroupedDropdownInput
                          key={`${customAttributeValue}`}
                          name={`${customAttributeFormValuesPrefix}${customAttributeValue}`}
                          label={`${customAttributeValue} Assignee`}
                          defaultGroup={
                            areCustomAttributeAssignmentFilterAssigneesGroup[
                              customAttributeValue
                            ]
                              ? UsersUserGroupsGroupedDropdownInputMode.GROUPS
                              : UsersUserGroupsGroupedDropdownInputMode.USERS
                          }
                          onActiveGroupChange={(isGroup) => {
                            setAreCustomAttributeAssignmentFilterAssigneesGroup(
                              (prev) => ({
                                ...prev,
                                [customAttributeValue]: isGroup,
                              })
                            );
                          }}
                          isRequired
                          isFluid
                        />
                      );
                    }
                  )}
                </>
              )}

              {assignmentMode === "assignmentFilterVariable" && (
                <>
                  <div className={cx("field")}>
                    <p className={cx("prompt", "dropdown")}>Variable</p>
                    <DropdownInputBare
                      name="variableName" // Name doesn't make a difference for an uncontrolled component
                      placeholder="Select a variable..."
                      value={selectedVariable}
                      onChange={(newValue) => {
                        setSelectedVariable(newValue);
                      }}
                      options={variableOptions}
                      isClearable={false}
                      labelKey="value"
                      valueKey="value"
                    />
                  </div>
                  {availableVariables.map((variableValue) => {
                    return (
                      <Card
                        key={`${variableValue}`}
                        className={cx("field")}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          flexDirection: "row",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                            flexWrap: "wrap",
                          }}
                        >
                          Assign to
                          <SingleAssigneeRender
                            assignee={
                              formValues[
                                `${variableFormValuesPrefix}${variableValue}`
                              ]
                            }
                          />
                          When equal to
                          <b>{variableValue}</b>
                        </div>
                        <Button
                          icon="minus-circle"
                          onClick={() => {
                            form.setValue(
                              //@ts-expect-error This field type definition doesn't exist on form values, but the value exists
                              `${variableFormValuesPrefix}${variableValue}`,
                              undefined
                            );
                          }}
                        />
                      </Card>
                    );
                  })}

                  <Button
                    icon="plus-circle"
                    style={{ width: "100%" }}
                    onClick={() => setIsNewVariableConditionDialogOpen(true)}
                  >
                    Add Variable Value
                  </Button>
                </>
              )}

              {assignmentMode === "userOrGroup" && (
                <div className={cx("field")}>
                  <UsersUserGroupsGroupedDropdownInput
                    name="assignee"
                    label="Assignee"
                    placeholder="Select an assignee..."
                    defaultGroup={
                      formValues.assignee?.[0] ??
                      UsersUserGroupsGroupedDropdownInputMode.USERS
                    }
                    isRequired
                    isFluid
                  />
                </div>
              )}
            </Accordion.AccordionPanel>
          </Accordion>
        </div>
      </Form>
      <AddNewVariableConditionDialog
        isOpen={isNewVariableConditionDialogOpen}
        onClose={() => setIsNewVariableConditionDialogOpen(false)}
        onAdd={(variableValue, assignmentFilter) => {
          form.setValue(
            //@ts-expect-error This field type definition doesn't exist on form values, but the value exists
            `${variableFormValuesPrefix}${variableValue}`,
            [
              assignmentFilter.assignee_group
                ? UsersUserGroupsGroupedDropdownInputMode.GROUPS
                : UsersUserGroupsGroupedDropdownInputMode.USERS,
              assignmentFilter.assignee_group ?? assignmentFilter.assignee_user,
            ]
          );
          setIsNewVariableConditionDialogOpen(false);
        }}
      />
    </>
  );
};

export default WorkflowConfigurationAssignee;
